// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGateSession } from './gateSession';
import {
  getFloorplan,
  saveFloorplan,
  getMasterSections,
} from './services/apiService';

const VESSELS = [
  { id: 1, name: 'Maria 1' },
  { id: 2, name: 'Maria 2' },
  { id: 3, name: 'Valesia' },
  { id: 4, name: 'Bar Bar' },
  { id: 5, name: 'Kalispera' },
  { id: 6, name: 'Infinity' },
  { id: 7, name: 'Perla' },
  { id: 8, name: 'Bob' },
];

// Fallback floorplan images για page2 (mirror του page2.tsx VESSEL_FLOORPLANS).
// Θα μεταφερθεί στη DB στο Stage 4 — μέχρι τότε hardcoded.
const VESSEL_FLOORPLANS_BY_NAME: Record<string, string> = {
  'bob': '/images/floorplans/lagoon-42.webp',
  'perla': '/images/floorplans/lagoon-46.png',
  'infinity': '/images/floorplans/bali-4-2-infinity.png',
  'maria 1': '/images/floorplans/jeanneau-449.png',
  'maria 2': '/images/floorplans/jeanneau-54.png',
  'bar bar': '/images/floorplans/beneteau-oceanis-46-new.png',
  'kalispera': '/images/floorplans/bavaria-c42-cruiser-kalispera.png',
  'valesia': '/images/floorplans/bavaria-c42-cruiser-valesia.png',
};

const PAGE_GROUPS: Array<{ key: string; label: string }> = [
  { key: 'page2', label: 'Page 2' },
  { key: 'page3', label: 'Page 3' },
  { key: 'page4', label: 'Page 4' },
];

type Hotspot = {
  id: string;
  x: number;
  y: number;
  title: string;
  category: string;
};

type Section = {
  section_key: string;
  title_el?: string;
  title_en?: string;
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

const HotspotEditorPage: React.FC = () => {
  const navigate = useNavigate();

  // Gate γίνεται στο AdminDashboard. Single-source gate object — never null —
  // ώστε το handleSave (κάτω) να πάρει σταθερό gate.code σε κάθε render.
  // Αν δεν υπάρχει session (gate.code κενό) → πίσω στο /admin.
  // Pattern mirrored από ItemsManagementPage.PerVesselTab (useGate fallback)
  // για να αποφευχθεί silent 401 όταν saveFloorplan καλείται με
  // employeeCode=undefined (X-Employee-Code header τότε γίνεται empty → 401 silent fail).
  const gate = getGateSession() || { code: '', name: '', role: '' };
  useEffect(() => {
    if (!gate.code) navigate('/admin');
  }, [gate.code, navigate]);

  const [vesselId, setVesselId] = useState<number>(1);
  const [pageGroup, setPageGroup] = useState<string>('page2');
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const [hotspots, setHotspots] = useState<Hotspot[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [statusMsg, setStatusMsg] = useState<string>('');
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const imageWrapRef = useRef<HTMLDivElement | null>(null);

  // Load sections για το επιλεγμένο page_group
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getMasterSections(pageGroup);
        if (!cancelled) setSections(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Failed to load sections:', e);
        if (!cancelled) setSections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [pageGroup]);

  // Load floorplan για το επιλεγμένο vessel + page_group
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setStatusMsg('');

      const v = VESSELS.find(x => x.id === vesselId);
      const vesselNameLower = (v?.name || '').toLowerCase();
      const fallbackImage = pageGroup === 'page2'
        ? (VESSEL_FLOORPLANS_BY_NAME[vesselNameLower] || '')
        : '';

      try {
        const fp = await getFloorplan(vesselId, pageGroup);
        if (cancelled) return;
        if (fp) {
          setBackgroundImage(fp.background_image || fallbackImage);
          setHotspots(Array.isArray(fp.hotspots) ? fp.hotspots : []);
        } else {
          setBackgroundImage(fallbackImage);
          setHotspots([]);
        }
      } catch (e) {
        console.error('Failed to load floorplan:', e);
        if (!cancelled) {
          setBackgroundImage(fallbackImage);
          setHotspots([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vesselId, pageGroup]);

  const sectionLabel = (key: string) => {
    const s = sections.find(x => x.section_key === key);
    if (!s) return key;
    return s.title_el || s.title_en || s.section_key;
  };

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Block creation αν είμαστε στο τέλος drag
    if (draggingId) return;
    // Skip αν το click ήταν πάνω σε κουκίδα (button stops propagation στο mousedown)
    const target = e.target as HTMLElement;
    if (target.closest('[data-hotspot]')) return;

    const rect = imageWrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;

    const x = clamp01((e.clientX - rect.left) / rect.width);
    const y = clamp01((e.clientY - rect.top) / rect.height);

    const newId = `hs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const defaultSection = sections[0]?.section_key || '';
    setHotspots(prev => [...prev, {
      id: newId,
      x,
      y,
      title: defaultSection ? sectionLabel(defaultSection).toUpperCase() : 'NEW',
      category: defaultSection,
    }]);
  };

  const updateHotspot = (id: string, patch: Partial<Hotspot>) => {
    setHotspots(prev => prev.map(h => h.id === id ? { ...h, ...patch } : h));
  };

  const deleteHotspot = (id: string) => {
    setHotspots(prev => prev.filter(h => h.id !== id));
  };

  // Drag (mouse + touch)
  const startDrag = (id: string) => (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setDraggingId(id);
  };

  useEffect(() => {
    if (!draggingId) return;

    const move = (clientX: number, clientY: number) => {
      const rect = imageWrapRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;
      const x = clamp01((clientX - rect.left) / rect.width);
      const y = clamp01((clientY - rect.top) / rect.height);
      setHotspots(prev => prev.map(h => h.id === draggingId ? { ...h, x, y } : h));
    };

    const onMouseMove = (ev: MouseEvent) => move(ev.clientX, ev.clientY);
    const onTouchMove = (ev: TouchEvent) => {
      if (ev.touches.length === 0) return;
      ev.preventDefault();
      move(ev.touches[0].clientX, ev.touches[0].clientY);
    };
    const onUp = () => setDraggingId(null);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [draggingId]);

  const handleSave = async () => {
    setSaving(true);
    setStatusMsg('');
    try {
      const ok = await saveFloorplan({
        vessel_id: vesselId,
        page_group: pageGroup,
        background_image: backgroundImage,
        hotspots,
      }, gate.code);
      setStatusMsg(ok ? '✅ Αποθηκεύτηκε' : '❌ Αποτυχία αποθήκευσης');
    } catch (e: any) {
      setStatusMsg('❌ Σφάλμα: ' + (e?.message || 'unknown'));
    } finally {
      setSaving(false);
    }
  };

  const vesselName = VESSELS.find(v => v.id === vesselId)?.name || '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-800">📍 Hotspot Editor</h1>
          <button
            onClick={() => navigate('/admin')}
            className="px-3 py-2 text-sm sm:text-base bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
          >
            ← Πίσω
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        {/* Toolbar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Σκάφος</label>
              <select
                value={vesselId}
                onChange={(e) => setVesselId(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {VESSELS.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Σελίδα</label>
              <select
                value={pageGroup}
                onChange={(e) => setPageGroup(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {PAGE_GROUPS.map(p => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Background image path</label>
              <input
                type="text"
                value={backgroundImage}
                onChange={(e) => setBackgroundImage(e.target.value)}
                placeholder="/images/floorplans/lagoon-42.webp"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-gray-500">
              {loading ? '⏳ Φόρτωση…' : `${hotspots.length} κουκίδες · ${vesselName} · ${pageGroup}`}
              {statusMsg && <span className="ml-3 font-medium">{statusMsg}</span>}
            </div>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-semibold disabled:opacity-50 hover:shadow-md transition"
            >
              {saving ? '⏳ Αποθήκευση…' : '💾 Αποθήκευση'}
            </button>
          </div>
        </div>

        {/* Editor canvas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="text-xs text-gray-500 mb-2">
            Click στην εικόνα για νέα κουκίδα · Drag για μετακίνηση · ✕ για διαγραφή
          </div>

          <div
            ref={imageWrapRef}
            onClick={handleImageClick}
            className="relative w-full bg-gray-100 rounded-lg overflow-hidden select-none"
            style={{ minHeight: '400px', cursor: draggingId ? 'grabbing' : 'crosshair' }}
          >
            {backgroundImage ? (
              <img
                src={backgroundImage}
                alt="floorplan"
                className="block w-full h-auto pointer-events-none"
                draggable={false}
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
              />
            ) : (
              <div className="flex items-center justify-center text-gray-400 text-sm py-32">
                Δώσε background image path παραπάνω για να ξεκινήσεις
              </div>
            )}

            {hotspots.map(h => (
              <div
                key={h.id}
                data-hotspot="1"
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: (h.x * 100) + '%', top: (h.y * 100) + '%', zIndex: 10 }}
              >
                <div
                  onMouseDown={startDrag(h.id)}
                  onTouchStart={startDrag(h.id)}
                  className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500 border-2 border-white shadow-lg"
                  style={{ cursor: draggingId === h.id ? 'grabbing' : 'grab' }}
                  title={h.title}
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-50 animate-ping pointer-events-none"></span>
                </div>

                {/* Section selector + delete (κάτω από κουκίδα) */}
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-1 flex items-center gap-1 bg-white rounded-md shadow-md border border-gray-200 px-1 py-0.5 whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <select
                    value={h.category}
                    onChange={(e) => {
                      const newKey = e.target.value;
                      updateHotspot(h.id, {
                        category: newKey,
                        title: sectionLabel(newKey).toUpperCase(),
                      });
                    }}
                    className="text-xs border-0 bg-transparent focus:outline-none max-w-[160px]"
                  >
                    <option value="">— διάλεξε —</option>
                    {sections.map(s => (
                      <option key={s.section_key} value={s.section_key}>
                        {sectionLabel(s.section_key)}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => deleteHotspot(h.id)}
                    className="text-xs px-1.5 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 rounded"
                    title="Διαγραφή"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Coordinates list (debug + verification) */}
          {hotspots.length > 0 && (
            <div className="mt-4 text-xs text-gray-600 font-mono space-y-1">
              {hotspots.map(h => (
                <div key={h.id}>
                  {h.category || '(no section)'} → x={h.x.toFixed(4)} y={h.y.toFixed(4)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotspotEditorPage;
